import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Briefcase, Container, LogOut, Package, ChevronRight } from "lucide-react";
import { useAuth } from "../../hooks/useAuth";
import { hasDeviceSession } from "../../utils/deviceSession";
import { useModules } from "../../hooks/useModules";
import type { ModuleKey } from "../../constants/modules";

/**
 * Driver landing / selection screen.
 *
 * Offers one tile per driver module this tenant has — each section then
 * provides a back button to return here. A tile is never shown for a module
 * that is switched off, because tapping it would only bounce the driver
 * straight back to this screen.
 *
 * Mobile responsive: single column on phones, cards side by side on wider
 * screens.
 */
const DriverHome: React.FC = () => {
  const { t } = useTranslation(["works", "containerOrders"]);
  const { user, logout } = useAuth();
  const { has } = useModules();
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/login");
    } catch {
      // non-fatal in demo
    }
  };

  /**
   * Whether this session can reach container work at all.
   *
   * A second condition on top of the module, answering a different question.
   * The module says whether this tenant has the driver container app; this says
   * whether *this* sign-in can use it. Containers are dispatched to a truck and
   * worked from the screen in its cab, so a driver on their own phone with an
   * email and password has no truck: the server refuses every container endpoint
   * to them, and a tile leading to a list that can only be empty would be an
   * invitation to a dead end.
   */
  const onTruck = hasDeviceSession();

  const allTiles: {
    key: string;
    module: ModuleKey;
    /** Reachable only from a truck's screen — see `onTruck`. */
    requiresTruck?: boolean;
    to: string;
    icon: React.ReactNode;
    iconClass: string;
    title: string;
    desc: string;
  }[] = [
    {
      key: "works",
      module: "driverWorks",
      to: "/works",
      icon: <Briefcase className="h-7 w-7" />,
      iconClass: "bg-[#FCEEE2] text-[#C97A3C]",
      title: t("driver.home.works.title"),
      desc: t("driver.home.works.desc"),
    },
    {
      key: "shipments",
      module: "driverShipments",
      to: "/shipments",
      icon: <Package className="h-7 w-7" />,
      iconClass: "bg-[#E7EFFB] text-[#3B6FB6]",
      title: t("driver.home.shipments.title"),
      desc: t("driver.home.shipments.desc"),
    },
    {
      key: "containers",
      module: "driverContainers",
      requiresTruck: true,
      to: "/driver/containers",
      icon: <Container className="h-7 w-7" />,
      iconClass: "bg-[#E6F2EC] text-[#2F7A5B]",
      title: t("containerOrders:driver.home.title"),
      desc: t("containerOrders:driver.home.desc"),
    },
  ];

  /**
   * Two filters, and they are deliberately not collapsed into one.
   *
   * `enabledTiles` is what this tenant has switched on. `tiles` is what this
   * particular session can actually open. The empty state below has to tell the
   * two apart: "no modules — contact your dispatcher" is right for the first and
   * badly wrong for the second, where the modules are fine and the driver simply
   * is not sitting in a truck.
   */
  const enabledTiles = allTiles.filter((tile) => has(tile.module));
  const tiles = enabledTiles.filter((tile) => !tile.requiresTruck || onTruck);

  return (
    <div className="min-h-full bg-[#FAFAF8] flex flex-col">
      {/* Header */}
      <div className="pt-safe bg-white border-b border-[#ECEAE4]">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#C97A3C] rounded-full flex items-center justify-center text-white font-semibold uppercase">
              {user?.firstName?.[0] || "D"}
            </div>
            <div>
              <div className="text-sm font-medium text-[#2B2F2E]">{user?.fullName}</div>
              <div className="text-xs text-[#8A9590]">{t("driver.home.role")}</div>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-[#5A6460] bg-white border border-[#E5E3DD] rounded-lg hover:bg-[#F5F4F0] transition-colors"
          >
            <LogOut className="h-4 w-4" />
            <span className="hidden sm:inline">{t("driver.list.logout")}</span>
          </button>
        </div>
      </div>

      {/* Selection */}
      <div className="flex-1 max-w-4xl w-full mx-auto px-4 py-10">
        <div className="mb-8 text-center sm:text-left">
          <h1 className="text-2xl font-bold text-[#0B0F0E]">
            {t("driver.home.greeting", { name: user?.firstName || "" })}
          </h1>
          <p className="text-sm text-[#8A9590] mt-1">{t("driver.home.subtitle")}</p>
        </div>

        {/* Tiles: two-up on tablets, three-up on desktop, so the last one
            never sits alone on a row of its own. */}
        {/*
          Shown for one cause of emptiness, not for both.

          "No modules have been enabled for your account yet — contact your
          dispatcher" is the right sentence when the tenant has switched
          everything off. It is the wrong one, and an expensive one, for a driver
          whose modules are fine but who signed in with a password rather than a
          PIN: their work exists, it is on the truck's screen, and this would send
          them to the office over nothing.

          So it keys off `enabledTiles`, which the truck-session filter has not
          touched. When modules are enabled and only the truck filter emptied the
          list, the screen stays bare — where container work actually lives is a
          question about containers, and it is answered where containers are.
        */}
        {enabledTiles.length === 0 && (
          <div className="bg-white border border-[#ECEAE4] rounded-2xl p-8 text-center">
            <p className="text-sm text-[#8A9590]">
              {t("driver.home.empty")}
            </p>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {tiles.map((tile) => (
            <button
              key={tile.key}
              onClick={() => navigate(tile.to)}
              className="group bg-white border border-[#ECEAE4] rounded-2xl p-6 text-left shadow-sm hover:shadow-md hover:border-[#C97A3C] transition-all"
            >
              <div className={`h-14 w-14 rounded-2xl flex items-center justify-center ${tile.iconClass}`}>
                {tile.icon}
              </div>
              <div className="mt-4 flex items-center justify-between">
                <h2 className="text-lg font-semibold text-[#0B0F0E]">{tile.title}</h2>
                <ChevronRight className="h-5 w-5 text-[#C7CCC9] group-hover:text-[#C97A3C] transition-colors" />
              </div>
              <p className="text-sm text-[#8A9590] mt-1">{tile.desc}</p>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default DriverHome;
